import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth relies on httpOnly cookies set by the backend; no tokens are kept in JS-accessible storage.
export const adminApi = axios.create({ baseURL: API, withCredentials: true });

export const formatApiError = (detail, fallback = "Something went wrong. Please try again.") => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AdminContext = createContext(null);

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anonymous
  const checking = useRef(false);

  const checkSession = useCallback(async () => {
    if (checking.current) return undefined;
    checking.current = true;
    try {
      const { data } = await adminApi.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    } finally {
      checking.current = false;
    }
  }, []);

  // Public visitors never hit /auth/me — the session is only probed on /admin routes.
  useEffect(() => {
    if (window.location.pathname.startsWith("/admin")) checkSession();
    else setUser(false);
  }, [checkSession]);

  const login = useCallback(async (email, password) => {
    const { data } = await adminApi.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminApi.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed; clearing local session anyway.", err?.message);
    }
    setUser(false);
  }, []);

  useEffect(() => {
    const id = adminApi.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401 && !err.config?.url?.includes("/auth/login")) setUser(false);
        return Promise.reject(err);
      }
    );
    return () => adminApi.interceptors.response.eject(id);
  }, []);

  return <AdminContext.Provider value={{ user, login, logout, checkSession }}>{children}</AdminContext.Provider>;
};

export const useAdmin = () => useContext(AdminContext);
